import { KEYBOARD_SHORTCUT_PASSTHROUGH_CLASS } from "./hooks";
import {
  createKeyMap,
  FALLTHROUGH,
  KeyCommandFunction,
  KeyShortcuts,
} from "./keyMap";
import { PlatformName } from "./platform";
import { getEventShortcutNames } from "./shortcuts";

const matchShortcut = (
  event: KeyboardEvent,
  platformName: PlatformName,
  shortcuts: KeyShortcuts,
  options: { invokedWithinInput?: boolean } = {}
): KeyCommandFunction | undefined => {
  const keyMap = createKeyMap(shortcuts, platformName);

  const eventShortcutNames = getEventShortcutNames(event, platformName);

  const matchingName = eventShortcutNames.find((name) => name in keyMap);

  if (!matchingName) return;

  const command = keyMap[matchingName];

  const commandObject = typeof command === "function" ? { command } : command;

  if (options.invokedWithinInput && !commandObject.allowInInput) return;

  return commandObject.command;
};

export const handleKeyboardEvent = (
  event: KeyboardEvent,
  platformName: PlatformName,
  shortcuts: KeyShortcuts,
  options: { invokedWithinInput?: boolean } = {}
) => {
  const command = matchShortcut(event, platformName, shortcuts, options);

  if (!command) return;

  const result = command();

  if (result !== FALLTHROUGH) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
};

export function elementShouldHandleOwnShortcut(target: EventTarget | null) {
  // Events without a target are not relevant for us here
  if (!(target instanceof HTMLElement)) return true;

  let element = target;

  const closestContentEditable: HTMLElement | null =
    element.closest("[contenteditable]");

  if (closestContentEditable) {
    element = closestContentEditable;
  }

  // If an element has the passthrough class, we should handle the event globally
  if (element.classList.contains(KEYBOARD_SHORTCUT_PASSTHROUGH_CLASS)) {
    return false;
  }

  // Check if we're within an input/textarea/contenteditable
  if (
    (element instanceof HTMLInputElement &&
      element.type !== "checkbox" &&
      element.type !== "radio") ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLIFrameElement ||
    element.hasAttribute("contenteditable")
  ) {
    return true;
  }

  return false;
}
